import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import EditableText from '@sayyesschool/frontend/components/editable-text';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { useSection } from '@sayyesschool/frontend/features/courses';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Grid } from '@sayyesschool/frontend/ui-components';

import SectionDescription from '../section-description';
import SectionExercises from '../section-exercises';

export default function SectionPage() {
    const navigate = useNavigate();
    const params = useParams();
    const { course, unit, lesson, section, actions } = useSection(params);

    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const handleUpdate = useCallback(data => {
        return actions.updateSection(course.id, section.id, data);
    }, [course, section, actions]);

    const handleUpdateTitle = useCallback(title => {
        return actions.updateSection(course.id, section.id, { title });
    }, [course, section, actions]);

    const handleDelete = useCallback(() => {
        return actions.deleteSection(course.id, section.id)
            .then(() => navigate(lesson.url));
    }, [course, lesson, section, actions, navigate]);

    if (!section) return <LoadingIndicator fullscreen />;

    return (
        <Page className="SectionPage">
            <Page.Header
                title={
                    <EditableText
                        content={section.title}
                        required
                        onUpdate={handleUpdateTitle}
                    />
                }
                breadcrumbs={[
                    { key: 'course', content: course.title, to: course.uri, title: 'Курс' },
                    { key: 'unit', content: unit.title, to: unit.uri, title: 'Юнит' },
                    { key: 'lesson', content: lesson.title, to: lesson.uri, title: 'Урок' }
                ]}
                actions={[
                    {
                        key: 'delete',
                        icon: 'delete',
                        title: 'Удалить секцию',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid spacing={2}>
                    <Grid.Item xs={7}>
                        <SectionExercises
                            section={section}
                            course={course}
                            onReorder={handleUpdate}
                        />
                    </Grid.Item>

                    <Grid.Item xs={5}>
                        <SectionDescription
                            section={section}
                            onUpdate={handleUpdate}
                        />
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <ConfirmationDialog
                title="Удалить секцию?"
                message={`Секция "${section.title}" будет удалена без возможности восстановления.`}
                open={isConfirmationDialogOpen}
                onConfirm={handleDelete}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}